import Constants from "app/shared/constants/constants";
import getBrandName from "app/shared/utils/BrandHelper";

export const CLIENT_META_HEADER = "client-meta";

const CLIENT_ORIGINS = ["web", "ios", "android"] as const;
const CLIENT_BRANDS = ["ebb", "bru", "nnyb"] as const;
const CLIENT_ENVIRONMENTS = ["dev", "qa", "prod"] as const;

export type ClientOrigin = (typeof CLIENT_ORIGINS)[number];
export type ClientBrand = (typeof CLIENT_BRANDS)[number];
export type ClientEnvironment = (typeof CLIENT_ENVIRONMENTS)[number];

export type ClientMeta = {
    originPlatform: ClientOrigin;
    brand: ClientBrand;
    env: ClientEnvironment;
    appVersion: string;
};

const IP_HOST_PATTERN = /^(\d{1,3}\.){3}\d{1,3}$/;

export const formatClientMeta = ({
    originPlatform,
    brand,
    env,
    appVersion,
}: ClientMeta): string => `${originPlatform}-${brand}-${env}-${appVersion}`;

export const isValidClientMeta = (value?: string): value is string => {
    if (!value || value.includes("\r") || value.includes("\n")) {
        return false;
    }

    const [origin, brand, environment, ...appVersionParts] = value.trim().split("-");
    return CLIENT_ORIGINS.some((allowedOrigin) => allowedOrigin === origin)
        && CLIENT_BRANDS.some((allowedBrand) => allowedBrand === brand)
        && CLIENT_ENVIRONMENTS.some((allowedEnvironment) => (
            allowedEnvironment === environment
        ))
        && appVersionParts.join("-").trim().length > 0;
};

export const resolveClientOrigin = (platform?: string): ClientOrigin => {
    const normalizedPlatform = platform?.toLowerCase();
    return CLIENT_ORIGINS.find((origin) => origin === normalizedPlatform) ?? "web";
};

export const resolveClientBrand = (appSource: string): ClientBrand => {
    switch (appSource.toLowerCase()) {
        case Constants.EBB_APP.toLowerCase():
            return "ebb";
        case Constants.BRU_APP.toLowerCase():
            return "bru";
        case Constants.NNYB_APP.toLowerCase():
            return "nnyb";
        default:
            throw new Error("Unsupported client brand");
    }
};

export const resolveClientEnvironment = (hostname: string): ClientEnvironment => {
    const normalizedHostname = hostname.toLowerCase();
    if (
        normalizedHostname === "localhost"
        || IP_HOST_PATTERN.test(normalizedHostname)
        || normalizedHostname.includes("-dev.")
    ) {
        return "dev";
    }
    if (
        normalizedHostname.includes("-qa.")
        || normalizedHostname.includes("-staging.")
    ) {
        return "qa";
    }
    return "prod";
};

export const resolveClientMeta = (
    defaults: ClientMeta,
    handoffClientMeta?: string,
): string => {
    if (isValidClientMeta(handoffClientMeta)) {
        return handoffClientMeta.trim();
    }

    return formatClientMeta(defaults);
};

export const getClientMeta = (): string => {
    const originPlatform = resolveClientOrigin(window.platform);
    const defaults: ClientMeta = {
        originPlatform,
        brand: resolveClientBrand(getBrandName().appSource),
        env: resolveClientEnvironment(window.location.hostname),
        appVersion: Constants.APP_VERSION,
    };
    const handoffClientMeta = originPlatform === "web"
        ? undefined
        : window.handOff?.clientMeta;

    return resolveClientMeta(defaults, handoffClientMeta);
};
